import { resolveAxisScale } from './axisScale.js'
import { applyEdit, baseTable } from './chartEdit.js'
import { retypeParagraphs } from './chartTextEdit.js'
import { formatChartNumber } from './numberFormat.js'

/**
 * RED-317 PR 3d-A — the chart model of an edited chart, derived on the
 * client so the canvas redraws as the admin types: the server's chart model
 * (ChartReader) with the override's sheet laid in and everything Word
 * computes from the data computed again — category slots and labels (a date
 * axis's month slots), Office's automatic value-axis scales and tick labels,
 * data-label texts, legend entries, titles and text boxes. Each step is a
 * port of the ChartReader method it names; for an unedited chart the result
 * is the server's model itself. The server stays authoritative: once saved,
 * the chart is read again from the part the compiler wrote.
 */

const MAX_TEXT = 1000
const MAX_DATE_SERIAL = 2958465
const DAY_MS = 86400000
const EPOCH = Date.UTC(1899, 11, 30)

/** An override that changes nothing derives nothing. */
export function isEmptyEdit(edit) {
  return !edit || Object.keys(edit).filter((key) => key !== 'v').length === 0
}

/**
 * @param {object} model the server's chart model (version 1, with `data`)
 * @param {object|null} edit the rawBlock's chartEdit
 * @returns {object} the model to lay out
 */
export function deriveChartModel(model, edit) {
  if (isEmptyEdit(edit) || !model?.data) return model
  const base = baseTable(model)
  const table = applyEdit(base, edit)
  const date1904 = Boolean(model.date1904)

  // Series: the server's own objects for existing ones, a copy of the
  // neighbour for new ones; raw values (no date-axis drop yet), names.
  const originals = new Map()
  for (const group of model.groups ?? []) for (const series of group.series ?? []) originals.set(series.idx, series)
  const groups = (model.groups ?? []).map((group) => ({ ...group, series: [] }))
  const sources = new Map()
  table.series.forEach((entry, order) => {
    const original = originals.get(entry.idx)
    const like = original ?? originals.get(entry.like) ?? null
    const group = groups[entry.group] ?? groups[0]
    if (!group || !like) return
    const series = original ? { ...original } : newSeries(like, entry.idx, model.autoColors?.[entry.idx] ?? null)
    series.name = entry.name
    series.values = [...entry.values]
    if (table.structural) series.order = order
    if (series.labels) {
      const rule = { ...series.labels.rule, name: entry.name }
      series.labels = { ...series.labels, rule, points: original ? series.labels.points : {}, items: labelItems(series.values, rule, original ? series.labels.points : {}, date1904) ?? [] }
    }
    sources.set(entry.idx, entry)
    group.series.push(series)
  })

  const axes = {}
  for (const [id, axis] of Object.entries(model.axes ?? {})) axes[id] = { ...axis }
  categories(groups, axes, table, model, sources, date1904)
  scaleValueAxes(groups, axes, date1904)

  const derived = { ...model, groups, axes }
  derived.legend = legend(model, groups)
  derived.title = model.title ? title(model.title, edit, groups) : null
  for (const axis of Object.values(axes)) {
    if (axis.title && edit.text?.[axis.title.key] !== undefined && axis.title.editable !== false) {
      axis.title = { ...axis.title, paragraphs: retypeParagraphs(axis.title.paragraphs, edit.text[axis.title.key]) }
    }
  }
  derived.shapes = (model.shapes ?? []).map((shape) =>
    edit.text?.[shape.key] !== undefined && shape.editable !== false ? { ...shape, paragraphs: retypeParagraphs(shape.paragraphs, edit.text[shape.key]) } : shape,
  )
  return derived
}

/** A new series as the compiler writes it: its neighbour's c:ser with Office's colour for its own idx. */
function newSeries(like, idx, color) {
  const series = { ...like, idx }
  if (color) {
    if (series.type === 'bar') {
      if (series.fill) series.fill = color
    } else if (series.line) {
      series.line = { ...series.line, color }
    }
    if (series.marker) {
      series.marker = { ...series.marker, fill: series.marker.fill && series.marker.fill !== 'none' ? color : series.marker.fill, line: series.marker.line ? { ...series.marker.line, color } : null }
    }
  }
  return series
}

/** ChartReader::labelItems(). */
export function labelItems(values, rule, points, date1904 = false) {
  const items = []
  for (let index = 0; index < values.length; index++) {
    const value = values[index]
    const point = points?.[index] ?? null
    if (point !== null ? point.delete : rule.delete) continue
    if (value === null || value === undefined) continue
    const flags = point ?? rule
    const parts = []
    if (flags.ser) parts.push(rule.name)
    if (flags.cat) parts.push('{cat}')
    if (flags.val) parts.push(formatChartNumber(value, rule.code, date1904))
    let custom = null
    if (Array.isArray(point?.custom)) {
      custom = point.custom.map((token) => (token.t === 'val' ? formatChartNumber(value, rule.code, date1904) : token.t === 'ser' ? rule.name : token.t === 'cat' ? '{cat}' : token.v)).join('')
    }
    if (!parts.length && custom === null) continue
    const text = custom ?? parts.join(rule.sep)
    if (new TextEncoder().encode(text).length > MAX_TEXT) return null
    const item = { index, text }
    if (point !== null) {
      if (point.layout) {
        item.dx = point.layout.dx
        item.dy = point.layout.dy
      }
      if (point.position) item.position = point.position
      if (point.style) item.style = point.style
    }
    items.push(item)
  }
  return items
}

// ------------------------------------------------------------- categories

/** A dense cache level → the sparse spans ChartReader::spans() makes. */
function spans(level, count) {
  const indices = []
  level.forEach((label, index) => {
    if (label !== null && label !== undefined) indices.push(index)
  })
  return indices.map((start, position) => {
    const end = (indices[position + 1] ?? count) - 1
    return { label: level[start], start, end: Math.max(start, Math.min(end, count - 1)) }
  })
}

/** The cache the axis reads: the edited sheet's categories once they changed, else the server's. */
function axisCache(axisId, table, model) {
  if (table.categoriesChanged) {
    if (table.kind === 'none') return null
    return { count: table.rows, levels: table.levels, numbers: table.numbers, formatCode: table.formatCode }
  }
  return model.data?.axisCaches?.[axisId] ?? null
}

/** ChartReader::categories(). */
function categories(groups, axes, table, model, sources, date1904) {
  for (const axis of Object.values(axes)) {
    if (axis.kind === 'val') continue
    const id = axis.id
    let pointCount = 0
    let onAxis = false
    for (const group of groups) {
      if (group.catAx !== id) continue
      for (const series of group.series) {
        pointCount = Math.max(pointCount, series.values.length)
        onAxis = true
      }
    }
    const cache = onAxis ? axisCache(id, table, model) : null
    const count = pointCount > 0 ? pointCount : (cache?.count ?? 0)
    if (axis.kind === 'date' && Array.isArray(cache?.numbers)) {
      dateCategories(axis, groups, cache.numbers.slice(0, cache.count), cache.formatCode, table, sources, date1904)
      continue
    }
    let levels
    if (cache?.numbers) {
      const code = axis.sourceLinked ? cache.formatCode : axis.formatCode
      const labels = cache.numbers.slice(0, cache.count).map((number) => (number === null ? '' : formatChartNumber(number, code, date1904)))
      levels = [labels]
    } else {
      levels = (cache?.levels ?? []).map((level) => level.slice(0, cache.count))
    }
    if (axis.noMultiLvlLbl && levels.length > 1) levels = [levels[0]]
    axis.count = count
    axis.levels = levels.map((level) => spans(level, count))
    delete axis.labelStep
    for (const group of groups) {
      if (group.catAx !== id) continue
      for (const series of group.series) series.slots = series.values.map((_, index) => index)
    }
  }
  for (const group of groups) {
    for (const series of group.series) series.slots ??= series.values.map((_, index) => index)
  }
}

function monthIndex(serial) {
  const date = new Date(EPOCH + Math.floor(serial) * DAY_MS)
  return date.getUTCFullYear() * 12 + date.getUTCMonth()
}

function serialOfMonth(index) {
  return Math.round((Date.UTC(Math.floor(index / 12), index % 12, 1) - EPOCH) / DAY_MS)
}

/** ChartReader::dateCategories(). */
function dateCategories(axis, groups, serials, sourceFormat, table, sources, date1904) {
  const known = serials.filter((serial) => serial !== null && serial !== undefined)
  const span = [...known]
  for (const end of ['min', 'max']) if (axis.fixed?.[end] !== null && axis.fixed?.[end] !== undefined) span.push(axis.fixed[end])
  let usable = known
  if (span.length && (Math.min(...span) < 0 || Math.max(...span) > MAX_DATE_SERIAL)) usable = []
  if (!usable.length) {
    axis.count = 0
    axis.levels = []
    return
  }
  let lastIndex = -1
  serials.forEach((serial, index) => {
    if (serial !== null && serial !== undefined) lastIndex = index
  })
  const lastMonth = monthIndex(serials[lastIndex])
  let reach = serials.length
  for (const group of groups) {
    for (const series of group.series) {
      const count = table.categoriesChanged ? (table.kind === 'date' || table.kind === 'number' ? table.rows : null) : sources.get(series.idx)?.categoryCount
      if (count !== null && count !== undefined) reach = Math.max(reach, count)
    }
  }
  const monthOf = (index) => {
    if (index >= serials.length) return index < reach ? lastMonth + index - lastIndex : null
    return serials[index] === null || serials[index] === undefined ? null : monthIndex(serials[index])
  }
  const first = axis.fixed?.min !== null && axis.fixed?.min !== undefined ? monthIndex(axis.fixed.min) : monthIndex(Math.min(...usable))
  const last = axis.fixed?.max !== null && axis.fixed?.max !== undefined ? monthIndex(axis.fixed.max) : monthIndex(Math.max(...usable))
  const count = Math.max(1, last - first + 1)
  if (count > 10000) {
    axis.count = 0
    axis.levels = []
    return
  }
  const step = Math.max(1, Math.trunc(axis.fixed?.majorUnit ?? 1))
  const code = axis.sourceLinked ? sourceFormat : axis.formatCode
  const labels = []
  for (let slot = 0; slot < count; slot += step) labels.push({ label: formatChartNumber(serialOfMonth(first + slot), code, date1904), start: slot, end: slot })
  axis.count = count
  axis.labelStep = step
  axis.levels = [labels]
  for (const group of groups) {
    if (group.catAx !== axis.id) continue
    for (const series of group.series) {
      const slots = []
      series.values = series.values.map((value, index) => {
        const pointMonth = monthOf(index)
        slots[index] = pointMonth === null ? null : pointMonth - first
        if (pointMonth === null || slots[index] < 0 || (slots[index] >= count && index < serials.length)) return null
        return value
      })
      series.slots = slots
    }
  }
}

// ----------------------------------------------------------------- scales

/** ChartReader::extents(). */
function extents(group) {
  const out = []
  if (group.grouping === 'stacked') {
    const positive = new Map()
    const negative = new Map()
    for (const series of group.series) {
      series.values.forEach((value, index) => {
        if (value === null || value === undefined) return
        const sums = value >= 0 ? positive : negative
        sums.set(index, (sums.get(index) ?? 0) + value)
      })
    }
    for (const sums of [positive, negative]) {
      if (sums.size) out.push([Math.min(0, ...sums.values()), Math.max(0, ...sums.values())])
    }
    if (out.length && negative.size === 0 && positive.size) out[0][0] = Math.min(...positive.values())
    return out
  }
  for (const series of group.series) {
    const known = series.values.filter((value) => value !== null && value !== undefined)
    if (known.length) out.push([Math.min(...known), Math.max(...known)])
  }
  return out
}

/** ChartReader::scaleValueAxes(). */
function scaleValueAxes(groups, axes, date1904) {
  for (const axis of Object.values(axes)) {
    if (axis.kind !== 'val' || axis.scaleLength === undefined) continue
    let low = null
    let high = null
    let format = null
    for (const group of groups) {
      if (group.valAx !== axis.id) continue
      for (const [min, max] of extents(group)) {
        low = low === null ? min : Math.min(low, min)
        high = high === null ? max : Math.max(high, max)
      }
      format ??= group.series[0]?.formatCode ?? null
    }
    const scale = resolveAxisScale(low, high, axis.fixed?.min ?? null, axis.fixed?.max ?? null, axis.fixed?.majorUnit ?? null, axis.scaleLength, Number(axis.text?.size ?? 10))
    const code = axis.sourceLinked ? (format ?? 'General') : axis.formatCode
    const ticks = []
    const steps = Math.round((scale.max - scale.min) / scale.majorUnit)
    for (let step = 0; step <= Math.min(steps, 200); step++) {
      const value = Number((scale.min + step * scale.majorUnit).toPrecision(12))
      ticks.push({ value, label: formatChartNumber(value, code, date1904) })
    }
    Object.assign(axis, scale, { ticks, dataRange: [low, high] })
  }
}

// ------------------------------------------------------- legend and titles

/** ChartReader::legend() entries: deleted entries stay deleted, new series join. */
function legend(model, groups) {
  if (!model.legend) return null
  const listed = new Set((model.legend.entries ?? []).map((entry) => entry.series))
  const deleted = new Set((model.data?.series ?? []).map((series) => series.idx).filter((idx) => !listed.has(idx)))
  const entries = []
  for (const group of groups) {
    for (const series of group.series) {
      if (deleted.has(series.idx)) continue
      entries.push({ series: series.idx, label: series.name, type: series.type, fill: series.fill, line: series.line, marker: series.marker })
    }
  }
  return { ...model.legend, entries }
}

function title(original, edit, groups) {
  const typed = edit.text?.[original.key]
  if (typed !== undefined && original.editable !== false) return { ...original, auto: false, paragraphs: retypeParagraphs(original.paragraphs, typed) }
  if (!original.auto) return original
  const series = groups.flatMap((group) => group.series)
  const text = series.length === 1 ? String(series[0].name) : 'Diagrammi tiitel'
  const paragraph = original.paragraphs?.[0] ?? { align: null, runs: [], style: null }
  return { ...original, paragraphs: [{ ...paragraph, runs: [{ text, style: paragraph.runs?.[0]?.style ?? paragraph.style }] }] }
}

/** The plain text a text target shows (title, axis title, shape). */
export { paragraphsText } from './chartTextEdit.js'
